import React, { useState } from "react";
import { Ticket } from "lucide-react";
import { dataTrigger, track } from "@/analytics";
import { activatePromo, appErrorMessage } from "@/api/app";
import { fetchMe } from "@/api/users";
import { useAppFeatures } from "@/store/appFeatures";
import { useAuth } from "@/store/authStore";
import { Button } from "@/ui/Button/Button";
import cls from "./styles.module.scss";

/**
 * «Есть промокод?» на главном экране. Видно, только когда промокоды включены
 * в админке (`/app/features/`). Действующую подписку промокод продлевает, иначе
 * оформляет новую — без автопродления.
 */
export function PromoCode() {
  const enabled = useAppFeatures((s) => s.promo);
  const loadFeatures = useAppFeatures((s) => s.load);
  const accessToken = useAuth((s) => s.accessToken);
  const setMe = useAuth((s) => s.setMe);

  const [open, setOpen] = useState(false);
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    const value = code.trim();
    if (!value || loading) return;

    setLoading(true);
    setError("");
    try {
      const result = await activatePromo(value);
      track("bkn.main.promo_success");

      const until = new Date(result.expires_at).toLocaleDateString("ru-RU");
      const warning = result.warning ? ` ${result.warning}` : "";
      setSuccess(`Промокод активирован, подписка до ${until}.${warning}`);
      setCode("");
      setOpen(false);

      try {
        setMe(await fetchMe());
      } catch {
        // не обновилось — новая дата появится при следующем открытии
      }
      loadFeatures();
    } catch (err) {
      setError(
        appErrorMessage(
          err,
          "Не удалось активировать промокод. Попробуйте ещё раз",
          "Промокоды пока недоступны"
        )
      );
    } finally {
      setLoading(false);
    }
  };

  if (!enabled || !accessToken) return null;

  return (
    <div className={cls.promo}>
      {success ? <p className={cls.success}>{success}</p> : null}

      {open ? (
        <form className={cls.form} onSubmit={submit}>
          <input
            className={cls.input}
            value={code}
            onChange={(e) => {
              setCode(e.target.value);
              setError("");
            }}
            placeholder="Промокод"
            aria-label="Промокод"
            autoCapitalize="characters"
            autoComplete="off"
            spellCheck={false}
            disabled={loading}
          />
          <Button
            type="submit"
            variant="primary"
            size="md"
            disabled={loading || !code.trim()}
            {...dataTrigger("bkn.main.promo_submit")}
          >
            {loading ? "Проверяем…" : "Активировать"}
          </Button>
          {error ? <p className={cls.error}>{error}</p> : null}
        </form>
      ) : (
        <button
          type="button"
          className={cls.link}
          onClick={() => {
            setOpen(true);
            setSuccess("");
          }}
          {...dataTrigger("bkn.main.promo_open")}
        >
          <Ticket className={cls.linkIcon} />
          Есть промокод?
        </button>
      )}
    </div>
  );
}
